import React, { useState, useEffect } from "react";
import { api, useAxiosInterceptor } from "../shared/axiosInstance";
import ThemeBarChart from "../shared/ThemeBarChart";
import BigFiveChart from "../shared/BigFiveChart";

const SimilarityValidator = () => {
    useAxiosInterceptor();

    const [user1Filter, setUser1Filter] = useState("");
    const [user2Filter, setUser2Filter] = useState("");
    const [matchData, setMatchData] = useState([]);
    const [sortConfig, setSortConfig] = useState({ key: 'similarityScore', direction: 'desc' });
    const [similarityData, setSimilarityData] = useState(null);
    const [user1Name, setUser1Name] = useState("");
    const [user2Name, setUser2Name] = useState("");
    const [user1LinkedIn, setUser1LinkedIn] = useState("");
    const [user2LinkedIn, setUser2LinkedIn] = useState("");
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [selectedMatchId, setSelectedMatchId] = useState(null);
    const [user1FilterInput, setUser1FilterInput] = useState("");
    const [user2FilterInput, setUser2FilterInput] = useState("");
    const [isSearching, setIsSearching] = useState(false);

    useEffect(() => {
        fetchMatches(page);
    }, [page, sortConfig]);

    const fetchMatches = async (pageToFetch, user1 = user1Filter, user2 = user2Filter) => {
        setIsSearching(true);
        try {
            const res = await api.get(`/matches/all-pairs`, {
                params: {
                    page: pageToFetch,
                    limit: 50,
                    user1Filter: user1,
                    user2Filter: user2,
                    sortKey: sortConfig.key,
                    sortDirection: sortConfig.direction
                }
            });
            setMatchData(res.data.matches);
            setTotalPages(res.data.totalPages);
        } catch (err) {
            console.error("❌ Error fetching matches:", err);
        } finally {
            setIsSearching(false);
        }
    };

    const handleCompare = async (match) => {
        const id1 = match.user1.id;
        const id2 = match.user2.id;
        if (selectedMatchId === match.matchId) {
            setSelectedMatchId(null);
            setSimilarityData(null);
            return;
        }
        setSelectedMatchId(match.matchId);
        setUser1Name(`${match.user1.firstName} ${match.user1.lastName}`);
        setUser2Name(`${match.user2.firstName} ${match.user2.lastName}`);
        setUser1LinkedIn(match.user1.linkedinProfile || "");
        setUser2LinkedIn(match.user2.linkedinProfile || "");

        try {
            const [r1, r2, sim] = await Promise.all([
                api.get(`/results/${id1}`),
                api.get(`/results/${id2}`),
                api.get(`/matches/validate/${id1}/${id2}`)
            ]);
            setSimilarityData({
                user1Results: r1.data,
                user2Results: r2.data,
                match: sim.data.match,
                differencesTable: sim.data.differencesTable,
                differencesSum: sim.data.differencesSum
            });
        } catch (err) {
            console.error("❌ Error fetching comparison data:", err);
        }
    };

    const handleSort = (key) => {
        const direction = sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
        setSortConfig({ key, direction });
    };

    return (
        <div className="p-4 bg-darkblue text-white grid grid-cols-[minmax(500px,700px)_1fr] gap-4 min-h-screen">
            <div className="flex flex-col gap-4">
                <div className="flex gap-2 items-center">
                    <input
                        type="text"
                        placeholder="Filter User 1 by name or email"
                        value={user1FilterInput}
                        onChange={(e) => setUser1FilterInput(e.target.value)}
                        className="p-2 rounded bg-deepblue text-white placeholder-gray-400 flex-grow"
                    />
                    {user1Filter && (
                        <button onClick={() => setUser1Filter('')} className="text-orange px-2">✕</button>
                    )}
                </div>
                <div className="flex gap-2 items-center">
                    <input
                        type="text"
                        placeholder="Filter User 2 by name or email"
                        value={user2FilterInput}
                        onChange={(e) => setUser2FilterInput(e.target.value)}
                        className="p-2 rounded bg-deepblue text-white placeholder-gray-400 flex-grow"
                    />
                    {user2Filter && (
                        <button onClick={() => setUser2Filter('')} className="text-orange px-2">✕</button>
                    )}
                </div>
                <div>
                    <button
                        onClick={() => {
                            setUser1Filter(user1FilterInput);
                            setUser2Filter(user2FilterInput);
                            setPage(1);
                            fetchMatches(1, user1FilterInput, user2FilterInput); // pass current input values directly
                        }}
                        className="ml-2 px-4 py-1 bg-blue-500 text-white rounded disabled:opacity-50"
                        disabled={isSearching}
                    >
                        {isSearching ? "Searching..." : "Search"}
                    </button>
                </div>
                <div className="overflow-auto h-full bg-bluegray p-3 rounded shadow">
                    <table className="w-full text-sm border border-white min-w-[600px]">
                        <thead>
                        <tr>
                            {['user1', 'user2', 'similarityScore'].map((key) => (

                                <th key={key} className="p-2 cursor-pointer text-orange" onClick={() => handleSort(key)}>
                                    {key}
                                </th>
                            ))}
                        </tr>
                        </thead>
                        <tbody>
                        {matchData.map((m) => (
                            <tr key={m.matchId} onClick={() => handleCompare(m)} className={`cursor-pointer hover:bg-deepblue ${selectedMatchId === m.matchId ? 'bg-orange' : ''}`}>
                                <td className="p-1">{m.user1.firstName} {m.user1.lastName}</td>
                                <td className="p-1">{m.user2.firstName} {m.user2.lastName}</td>
                                <td>{m.similarityScore !== undefined ? (m.similarityScore * 100).toFixed(2) + '%' : "N/A"}</td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                    <div className="flex justify-between mt-2">
                        <button onClick={() => setPage(p => Math.max(p - 1, 1))} disabled={page === 1} className="px-3 py-1 bg-orange rounded">Prev</button>
                        <span>Page {page} of {totalPages}</span>
                        <button onClick={() => setPage(p => Math.min(p + 1, totalPages))} disabled={page === totalPages} className="px-3 py-1 bg-orange rounded">Next</button>
                    </div>
                </div>
            </div>

            {similarityData && (
                <div className="flex flex-col gap-6">
                    <div className="bg-bluegray rounded-lg p-4 shadow text-center">
                        <p><strong>Similarity Score:</strong> {(similarityData.match.similarityScore * 100).toFixed(2)}%</p>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <div className="bg-bluegray p-4 rounded shadow">
                            {user1LinkedIn ? (
                                <p className="mb-2 text-center">
                                    <a href={user1LinkedIn.startsWith("http") ? user1LinkedIn : `https://${user1LinkedIn}`} target="_blank" rel="noopener noreferrer" className="text-blue-400 underline">
                                        {user1Name}'s LinkedIn
                                    </a>
                                </p>
                            ) : (
                                <h2 className="text-center text-orange font-bold" title="No LinkedIn available">
                                    {user1Name} Graphs
                                </h2>
                            )}
                            <ThemeBarChart themeScores={similarityData.user1Results.occupationalThemeScores} subscaleScores={similarityData.user1Results.occupationalSubscaleScores} />
                            <BigFiveChart bigFiveScores={similarityData.user1Results.bigFiveScores} />
                        </div>
                        <div className="bg-bluegray p-4 rounded shadow">
                            {user2LinkedIn ? (
                                <p className="mb-2 text-center">
                                    <a href={user2LinkedIn.startsWith("http") ? user2LinkedIn : `https://${user2LinkedIn}`} target="_blank" rel="noopener noreferrer" className="text-blue-400 underline">
                                        {user2Name}'s LinkedIn
                                    </a>
                                </p>
                            ) : (
                                <h2 className="text-center text-orange font-bold">{user2Name} Graphs</h2>
                        )}
                            <ThemeBarChart themeScores={similarityData.user2Results.occupationalThemeScores} subscaleScores={similarityData.user2Results.occupationalSubscaleScores} />
                            <BigFiveChart bigFiveScores={similarityData.user2Results.bigFiveScores} />
                        </div>
                    </div>

                    <div className="bg-bluegray rounded p-4 shadow overflow-x-auto">
                        <h2 className="text-orange text-lg font-bold mb-2 text-center">
                            Differences Table (Total: {similarityData.differencesSum})
                        </h2>
                        <table className="w-full text-white border text-sm">
                            <thead className="bg-gray-800">
                            <tr>
                                <th className="p-2">Theme</th>
                                <th className="p-2">Subscale</th>
                                <th className="p-2">Question</th>
                                <th className="p-2">{user1Name} Response</th>
                                <th className="p-2">{user2Name} Response</th>
                                <th className="p-2">Abs Diff</th>
                            </tr>
                            </thead>
                            <tbody>
                            {similarityData.differencesTable.map((item, idx) => (
                                <tr key={idx} className="border-b border-white">
                                    <td className="p-2">{item.theme}</td>
                                    <td className="p-2">{item.subscale}</td>
                                    <td className="p-2">{item.questionText}</td>
                                    <td className="p-2 text-center">{item.user1Response}</td>
                                    <td className="p-2 text-center">{item.user2Response}</td>
                                    <td className="p-2 text-center font-bold">{item.absoluteDifference}</td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SimilarityValidator;
